import { safeInvokeNoArg } from '../../utils/function';
import { getSystemParam } from '../platform';
import {
  postJsMessage,
  registerSpecificNotificationHandler,
  unregisterSpecificNotificationHandler,
} from '../../bridge';
import { Platform } from '../../utils/platform';
import { isAvailable, isInUnifyWebview } from 'jssdk/utils';
import { NativeAPIResponseMessage } from '../../utils/type';
import { PostMessageOption } from 'types/index';
import API from '../../api';

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.getClientTheme
 *
 * [PLATFORM]
 * mac        5.17.0+
 * windows    5.17.0+
 * linux      5.17.0+
 * ios        5.17.0+
 * android    5.17.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function getClientTheme() {
  try {
    const param = await getSystemParam();
    return param.clientTheme;
  } catch (e) {
    console.warn(
      '[hybrid/jssdk] getSystemParam failed, fallback to window.matchMedia',
      e
    );
    if (window.matchMedia?.('(prefers-color-scheme: dark)')?.matches) {
      return 'dark';
    }
    if (window.matchMedia?.('(prefers-color-scheme: light)')?.matches) {
      return 'light';
    }
    return 'light';
  } finally {
    refreshThemeSkinInternalListener();
  }
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.getClientSkin
 *
 * [PLATFORM]
 * mac        6.1.0+
 * windows    6.1.0+
 * linux      6.1.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function getClientSkin() {
  try {
    const param = await getSystemParam();
    return param.skin || 'skin_default';
  } catch (e) {
    console.warn(
      '[hybrid/jssdk] getSystemParam failed for getClientSkin, fallback to window.matchMedia',
      e
    );
    return 'skin_default';
  } finally {
    refreshThemeSkinInternalListener();
  }
}

export enum EmojiReactionSkinTone {
  Unknown = 0, // invalid
  Tone1 = 1, // default, unicode: ""
  Tone2, // unicode: "1f3fb"
  Tone3, // unicode: "1f3fc"
  Tone4, // unicode: "1f3fd"
  Tone5, // unicode: "1f3fe"
  Tone6, // unicode: "1f3ff"
}

export type EmojiReactionSkin = {
  emojiReactionSkin: EmojiReactionSkinTone;
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.getEmojiReactionSkin
 *
 * [PLATFORM]
 * mac        6.6.0+
 * windows    6.6.0+
 * linux      6.6.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function getEmojiReactionSkin(
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<EmojiReactionSkin>> {
  await isAvailable('theme.getEmojiReactionSkin');
  return postJsMessage<{}, EmojiReactionSkin>(
    API.getEmojiReactionSkin,
    {},
    options
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.registerThemeChangeListener
 *
 * [PLATFORM]
 * mac        5.17.0+
 * windows    5.17.0+
 * linux      5.17.0+
 * ios        5.17.0+
 * android    5.17.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export type Theme = 'light' | 'dark';
export type Skin = 'skin_rose' | 'skin_agave' | 'skin_bloom' | 'skin_default';
export function registerThemeChangeListener(
  onThemeSkinChange: ThemeSkinChangeCallback
) {
  onClientSkinChange(onThemeSkinChange);
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.unregisterThemeChangeListener
 *
 * [PLATFORM]
 * mac        5.17.0+
 * windows    5.17.0+
 * linux      5.17.0+
 * ios        5.17.0+
 * android    5.17.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function unregisterThemeChangeListener(
  onThemeSkinChange: ThemeSkinChangeCallback
) {
  offClientSkinChange(onThemeSkinChange);
}

const SkinChangeNotification = 'UnifyNotif_OnClientSkinChange';
export type ThemeSkin = {
  skin: Skin;
  theme: Theme;
};
export type ThemeSkinChangeCallback = (data: ThemeSkin) => void;
let onThemeSkinChangeFns: ThemeSkinChangeCallback[] = [];
let isThemeSkinChangeRegistered = false;
const darkModeMediaQuery: MediaQueryList = window.matchMedia?.(
  '(prefers-color-scheme: dark)'
);
let fallbackHandler: (event: MediaQueryListEvent) => void;
const onSkinChangeHandler = (message: NativeAPIResponseMessage<ThemeSkin>) => {
  const { returnCode, result } = message;
  if (returnCode === 0) {
    const { skin, theme } = result;
    if (skin) {
      onThemeSkinChangeFns.forEach((fn) => {
        safeInvokeNoArg(() => fn({ skin, theme }));
      });
      if (window.unifyConfig?.systemParam) {
        if (skin) {
          window.unifyConfig.systemParam.skin = skin;
        }

        if (theme) {
          window.unifyConfig.systemParam.clientTheme = theme;
        }
      }
    } else {
      console.warn(`OnClientSkinChange empty skin message`, message);
    }
  } else {
    console.warn('OnClientSkinChange failed', message);
  }
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.onClientSkinChange
 *
 * [PLATFORM]
 * mac        6.1.0+
 * windows    6.1.0+
 * linux      6.1.0+
 * ios        6.2.0+
 * android    6.2.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function onClientSkinChange(onSkinChangeFn: ThemeSkinChangeCallback) {
  onThemeSkinChangeFns.push(onSkinChangeFn);

  if (!isThemeSkinChangeRegistered) {
    isThemeSkinChangeRegistered = true;
    registerSpecificNotificationHandler(
      SkinChangeNotification,
      onSkinChangeHandler
    );
  }

  if (!isInUnifyWebview() && darkModeMediaQuery) {
    fallbackHandler = (e) => {
      onSkinChangeHandler(normalizeThemeChangeData(e));
    };
    if (darkModeMediaQuery.addEventListener) {
      darkModeMediaQuery.removeEventListener('change', fallbackHandler);
      darkModeMediaQuery.addEventListener('change', fallbackHandler);
    } else {
      darkModeMediaQuery.removeListener(fallbackHandler);
      darkModeMediaQuery.addListener(fallbackHandler);
    }
  }
}

function normalizeThemeChangeData(
  event: MediaQueryListEvent
): NativeAPIResponseMessage<ThemeSkin> {
  return {
    returnCode: 0,
    jsCallId: '',
    result: {
      theme: event.matches ? 'dark' : 'light',
      skin: 'skin_default' as Skin,
    },
  };
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.offClientSkinChange
 *
 * [PLATFORM]
 * mac        6.1.0+
 * windows    6.1.0+
 * linux      6.1.0+
 * ios        6.2.0+
 * android    6.2.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function offClientSkinChange(onSkinChangeFn: ThemeSkinChangeCallback) {
  onThemeSkinChangeFns = onThemeSkinChangeFns.filter(
    (fn) => fn !== onSkinChangeFn
  );
  if (onThemeSkinChangeFns.length === 0) {
    unregisterSpecificNotificationHandler(
      SkinChangeNotification,
      onSkinChangeHandler
    );
    isThemeSkinChangeRegistered = false;
  }

  if (!isInUnifyWebview() && darkModeMediaQuery) {
    if (darkModeMediaQuery.removeEventListener) {
      darkModeMediaQuery.removeEventListener('change', fallbackHandler);
    } else {
      darkModeMediaQuery.removeListener(fallbackHandler);
    }
  }
}

const webviewThemeChangeHandler = (e: MediaQueryListEvent) => {
  const isDark = e.matches;
  onThemeChangeFns.forEach((fn) =>
    safeInvokeNoArg(() => fn({ theme: isDark ? 'dark' : 'light' }))
  );
};

export type ThemeChangeCallback = (data: { theme: 'light' | 'dark' }) => void;
let onThemeChangeFns: ThemeChangeCallback[] = [];
/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.onWebviewThemeChange
 *
 * [PLATFORM]
 * mac        6.1.0+
 * windows    6.1.0+
 * linux      6.1.0+
 * ios        6.2.0+
 * android    6.2.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function onWebviewThemeChange(onThemeChange: ThemeChangeCallback) {
  if (Platform.isIOS() || Platform.isMacOS()) {
    darkModeMediaQuery.removeEventListener('change', webviewThemeChangeHandler);
    darkModeMediaQuery.addEventListener('change', webviewThemeChangeHandler);
    onThemeChangeFns.push(onThemeChange);
  } else {
    onClientSkinChange(onThemeChange);
  }
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * theme.offWebviewThemeChange
 *
 * [PLATFORM]
 * mac        6.1.0+
 * windows    6.1.0+
 * linux      6.1.0+
 * ios        6.2.0+
 * android    6.2.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function offWebviewThemeChange(onThemeChange: ThemeChangeCallback) {
  if (Platform.isIOS() || Platform.isMacOS()) {
    onThemeChangeFns = onThemeChangeFns.filter((fn) => fn !== onThemeChange);
    if (onThemeChangeFns.length === 0) {
      darkModeMediaQuery.removeEventListener(
        'change',
        webviewThemeChangeHandler
      );
    }
  } else {
    offClientSkinChange(onThemeChange);
  }
}

const noopListener = () => {};
export function refreshThemeSkinInternalListener() {
  offClientSkinChange(noopListener);
  offWebviewThemeChange(noopListener);
  onClientSkinChange(noopListener);
  onWebviewThemeChange(noopListener);
}

export default {
  getClientTheme,
  getClientSkin,
  getEmojiReactionSkin,
  registerThemeChangeListener,
  unregisterThemeChangeListener,
  onClientSkinChange,
  offClientSkinChange,
  onWebviewThemeChange,
  offWebviewThemeChange,
};
